// Scale bridge dashboard - what this dashboard is connected to, and how.

import { Button, Card, Descriptions, Popconfirm, Space, Tag, Typography } from 'antd';
import { DisconnectOutlined, ReloadOutlined } from '@ant-design/icons';

import { useAuthStore } from '../state/authStore';
import { REFRESH_MS, useReadingsStore } from '../state/readingsStore';
import { relative, timestamp } from '../lib/format';

const { Text, Paragraph } = Typography;

/** What the live feed is doing, said the same way the header pill says it. */
function liveFeedTag(status: 'connecting' | 'live' | 'offline') {
    if (status === 'live') return <Tag color="success">Connected</Tag>;
    if (status === 'connecting') return <Tag color="warning">Reconnecting</Tag>;
    return <Tag>Not connected</Tag>;
}

export default function Settings() {
    const session = useAuthStore((s) => s.session)!;
    const signOut = useAuthStore((s) => s.signOut);
    const reset = useReadingsStore((s) => s.reset);
    const refresh = useReadingsStore((s) => s.refresh);
    const loading = useReadingsStore((s) => s.loading);
    const lastRefreshAt = useReadingsStore((s) => s.lastRefreshAt);
    const liveStatus = useReadingsStore((s) => s.liveStatus);
    const stats = useReadingsStore((s) => s.stats);

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {/* Who this computer is signed in as. It was nowhere on the screen,
                which made the disabled buttons on Devices unexplainable — an
                operator could not see that they were signed in as one. */}
            <Card title="This computer">
                <Descriptions column={{ xs: 1, sm: 2 }} size="small" bordered>
                    <Descriptions.Item label="Signed in as">{session.username ?? '—'}</Descriptions.Item>
                    <Descriptions.Item label="Account">{session.accountName ?? '—'}</Descriptions.Item>
                    <Descriptions.Item label="Role">
                        <span style={{ textTransform: 'capitalize' }}>{session.role ?? 'operator'}</span>
                    </Descriptions.Item>
                    <Descriptions.Item label="Receiver">
                        <span className="mono">{session.baseUrl || 'same origin'}</span>
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            <Card
                title="Connection"
                extra={
                    <Button
                        icon={<ReloadOutlined />}
                        loading={loading}
                        onClick={() => refresh(session)}
                    >
                        Refresh now
                    </Button>
                }
            >
                <Descriptions column={{ xs: 1, sm: 2 }} size="small" bordered>
                    <Descriptions.Item label="Live feed">{liveFeedTag(liveStatus)}</Descriptions.Item>
                    <Descriptions.Item label="Background refresh">
                        every {Math.round(REFRESH_MS / 1000)}s
                    </Descriptions.Item>
                    <Descriptions.Item label="Last refreshed">
                        {lastRefreshAt ? (
                            <span className="num">
                                {timestamp(new Date(lastRefreshAt).toISOString())}{' '}
                                <Text type="secondary">
                                    ({relative(new Date(lastRefreshAt).toISOString())})
                                </Text>
                            </span>
                        ) : (
                            '—'
                        )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Weighings recorded">
                        <span className="num">{(stats?.totalReadings ?? 0).toLocaleString('en-IN')}</span>
                    </Descriptions.Item>
                    <Descriptions.Item label="Session">
                        {/* Was "View key: stored on this computer", from when the
                            dashboard was opened with a shared VIEW_KEY. That credential
                            is gone - it named nobody, could not be scoped to an account
                            and travelled in the URL - and the label outlived it, telling
                            operators their access rested on something that no longer
                            exists. */}
                        <Tag>signed in, expires after 24h</Tag>
                    </Descriptions.Item>
                </Descriptions>

                {/* ─── Rewritten, because it had stopped being true ───────────
                    This paragraph still said the dashboard polls "rather than
                    holding a live connection", which was correct until the live
                    feed shipped and wrong afterwards. It described the app as
                    slower than it is, and it contradicted the "Live" pill in the
                    header — leaving an operator to decide which of the two the
                    software meant. */}
                <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 16, marginBottom: 0 }}>
                    Two things keep this screen current. A live connection pushes each
                    weighing as it is printed, which is what the header means by
                    &ldquo;Live&rdquo;. Behind it, a refresh every{' '}
                    {Math.round(REFRESH_MS / 1000)} seconds re-reads the totals and
                    fills any gap left by a dropped connection, so the figures stay
                    right even when the live feed is down.
                </Paragraph>
            </Card>

            <Card title="Disconnect">
                <Paragraph type="secondary" style={{ fontSize: 13 }}>
                    Ends the session on this computer and clears the weighings held in
                    the browser. Nothing on the receiver changes, and no weighing is
                    deleted.
                </Paragraph>
                <Popconfirm
                    title="Disconnect from this receiver?"
                    description="You will need to sign in again."
                    okText="Disconnect"
                    okButtonProps={{ danger: true }}
                    cancelText="Cancel"
                    onConfirm={() => {
                        reset();
                        signOut();
                    }}
                >
                    <Button danger icon={<DisconnectOutlined />}>
                        Disconnect
                    </Button>
                </Popconfirm>
            </Card>

            <Card title="About">
                <Text type="secondary" style={{ fontSize: 13 }}>
                    Reads the receiver's operator API with your signed-in session, which
                    is scoped to this account and grants read access only. The device
                    credential the ESP32 uses to submit weighings is never held by this
                    dashboard — see the note in{' '}
                    <span className="mono">src/api/client.ts</span>.
                </Text>
            </Card>
        </Space>
    );
}
